import { DATE_FORMAT_YMD_DOTTED } from '@/constants/date-format';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const getOrdinalSuffix = (n: number) => {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
};

const formatEnMonthName = (date: Date) => new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
const formatEnDayOrdinal = (date: Date) => {
    const d = date.getDate();
    return `${d}${getOrdinalSuffix(d)}`;
};

export const formatValidDateString = (dateStr?: string | Date, formatStr: string = DATE_FORMAT_YMD_DOTTED): string | undefined => {
    if (!dateStr) {
        return undefined;
    }

    const parsedDate = dayjs(dateStr);

    if (!parsedDate.isValid()) {
        return undefined;
    }

    return parsedDate.format(formatStr);
};

export const formatKoreanDate = (dateString: string, t: TFunction): string => {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dayOfWeek = t(`common.weekdays.${WEEKDAY_KEYS[date.getDay()]}`);

    return `${day}/${month}(${dayOfWeek})`;
};

export const formatKoreanDateTTS = (start: string, end: string, t: TFunction<'translation', undefined>): string => {
    if (!start) return '';

    const dateStart = new Date(start);
    const dateEnd = new Date(end);

    if (isNaN(dateStart.getTime())) return '';

    const monthStart = String(dateStart.getMonth() + 1).padStart(2, '0');
    const dayStart = String(dateStart.getDate()).padStart(2, '0');
    const dayOfWeekStart = t(`common.weekdays_tts.${WEEKDAY_KEYS[dateStart.getDay()]}`);
    const monthNameStart = formatEnMonthName(dateStart);
    const dayOrdinalStart = formatEnDayOrdinal(dateStart);

    if (!end) {
        return t('common.tts.ongoing_from_date', {
            month: monthStart,
            day: dayStart,
            weekday: dayOfWeekStart,
            monthName: monthNameStart,
            dayOrdinal: dayOrdinalStart,
        });
    }

    const monthEnd = String(dateEnd.getMonth() + 1).padStart(2, '0');
    const dayEnd = String(dateEnd.getDate()).padStart(2, '0');
    const dayOfWeekEnd = t(`common.weekdays_tts.${WEEKDAY_KEYS[dateEnd.getDay()]}`);
    const monthNameEnd = formatEnMonthName(dateEnd);
    const dayOrdinalEnd = formatEnDayOrdinal(dateEnd);

    return t('common.tts.day', {
        month: monthStart,
        day: dayStart,
        weekday: dayOfWeekStart,
        month2: monthEnd,
        day2: dayEnd,
        weekday2: dayOfWeekEnd,
        monthName: monthNameStart,
        dayOrdinal: dayOrdinalStart,
        monthName2: monthNameEnd,
        dayOrdinal2: dayOrdinalEnd,
    });
};

export const formatListTypeDateRange = (begin: string, end: string, t: TFunction): string => {
    if (!end) {
        return `${formatKoreanDate(begin, t)} ~ ${t('common.date_always')}`;
    }

    return `${formatKoreanDate(begin, t)} ~ ${formatKoreanDate(end, t)}`;
};
